import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Divider,
  Skeleton,
  alpha,
  useTheme,
  Grid,
  styled,
  keyframes,
} from "@mui/material";
import {
  CakeOutlined,
  FavoriteOutlined,
  Today,
  DateRange,
  AllInclusive,
  NavigateBefore,
  NavigateNext,
  WhatsApp,
  Phone,
  OpenInNewOutlined,
  ViewCarouselOutlined,
  ViewListOutlined,
  InfoOutlined,
  AutoAwesome,
  CelebrationOutlined,
  TableView,
  PictureAsPdf,
  LocationOnOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import moment from "moment";
import { capitalizeWords } from "functions";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import CelebrationDetailsDialog from "./CelebrationDetailsDialog";
import {
  exportCelebrationsToExcel,
  exportCelebrationsToPDF,
} from "./dashboardExportUtils";

// ─── STYLED & ANIMATED COMPONENTS ──────────────────────────────────────────

const pulseGlow = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
`;

const GlassCard = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.65)
      : alpha(theme.palette.background.paper, 0.94),
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(
    theme.palette.mode === "dark" ? "#fff" : theme.palette.primary.main,
    0.08
  )}`,
  borderRadius: 20,
  boxShadow:
    theme.palette.mode === "dark"
      ? `0 8px 32px ${alpha(theme.palette.common.black, 0.35)}`
      : `0 6px 24px ${alpha(theme.palette.primary.main, 0.06)}`,
  overflow: "hidden",
  position: "relative",
}));

const ShowcaseCard = styled(Box)(({ theme, primarycolor, primarydark }) => ({
  position: "relative",
  padding: theme.spacing(3),
  borderRadius: 18,
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(primarycolor, 0.15)} 0%, ${alpha(
        theme.palette.background.paper,
        0.8
      )} 100%)`
      : `linear-gradient(135deg, ${alpha(primarycolor, 0.08)} 0%, ${alpha(
        "#ffffff",
        0.9
      )} 100%)`,
  border: `1.5px solid ${alpha(primarycolor, 0.25)}`,
  boxShadow: `0 8px 30px ${alpha(primarycolor, 0.12)}`,
  transition: "all 0.3s ease",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -40,
    right: -40,
    width: 140,
    height: 140,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${alpha(primarycolor, 0.2)} 0%, transparent 70%)`,
    pointerEvents: "none",
  },
}));

export default function CelebrationsWidget() {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();

  const [category, setCategory] = useState("Birthday"); // "Birthday" | "Anniversary"
  const [dayFilter, setDayFilter] = useState("Today"); // "Today" | "Tomorrow" | "All"
  const [viewMode, setViewMode] = useState("card"); // "card" | "list"
  const [currentIndex, setCurrentIndex] = useState(0);

  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const isBirthday = category === "Birthday";
  const primaryColor = isBirthday ? "#F59E0B" : "#EC4899";
  const primaryDark = isBirthday ? "#D97706" : "#BE185D";

  // Fetch both datasets
  useEffect(() => {
    let mounted = true;
    const fetchGreetings = async () => {
      setLoading(true);
      try {
        const [bdayRes, annivRes] = await Promise.all([
          axiosPrivate.post("LoadReferenceBirthday.php"),
          axiosPrivate.post("LoadReferenceAnniversary.php"),
        ]);
        if (!mounted) return;
        if (bdayRes.data?.status === 1 && Array.isArray(bdayRes.data?.Data)) {
          setBirthdays(bdayRes.data.Data);
        }
        if (annivRes.data?.status === 1 && Array.isArray(annivRes.data?.Data)) {
          setAnniversaries(annivRes.data.Data);
        }
      } catch (err) {
        console.error("Error loading celebration data:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchGreetings();
    return () => {
      mounted = false;
    };
  }, [axiosPrivate]);

  // Current active data list based on category
  const activeList = useMemo(() => {
    return isBirthday ? birthdays : anniversaries;
  }, [isBirthday, birthdays, anniversaries]);

  // Stats for current category (only Today and Tomorrow)
  const stats = useMemo(() => {
    const today = activeList.filter((c) => c?.DateCategory === "Today").length;
    const tomorrow = activeList.filter((c) => c?.DateCategory === "Tomorrow").length;
    const total = today + tomorrow;
    return { today, tomorrow, total };
  }, [activeList]);

  // Overall counts for category tabs (Today + Tomorrow)
  const bdayTotalCount = useMemo(() => birthdays.filter((c) => c?.DateCategory === "Today" || c?.DateCategory === "Tomorrow").length, [birthdays]);
  const annivTotalCount = useMemo(() => anniversaries.filter((c) => c?.DateCategory === "Today" || c?.DateCategory === "Tomorrow").length, [anniversaries]);

  // Auto-switch to Tomorrow if Today is empty and Tomorrow has celebrants
  useEffect(() => {
    if (dayFilter === "Today" && stats.today === 0 && stats.tomorrow > 0) {
      setDayFilter("Tomorrow");
    }
  }, [stats.today, stats.tomorrow, dayFilter]);

  // Filtered items (strictly Today and Tomorrow)
  const filteredList = useMemo(() => {
    return activeList.filter((item) => {
      if (dayFilter === "Both") {
        return item?.DateCategory === "Today" || item?.DateCategory === "Tomorrow";
      }
      return item?.DateCategory === dayFilter;
    });
  }, [activeList, dayFilter]);

  // Reset index when filter or category changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [category, dayFilter]);

  // If currentIndex goes out of bounds
  useEffect(() => {
    if (currentIndex >= filteredList.length && filteredList.length > 0) {
      setCurrentIndex(0);
    }
  }, [filteredList.length, currentIndex]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, filteredList.length - 1));
  }, [filteredList.length]);

  const handleOpenModal = useCallback((item) => {
    setSelectedItem(item);
    setModalOpen(true);
  }, []);

  const handleWhatsApp = useCallback(
    (mobile, name) => {
      if (!mobile) return;
      const firstName = capitalizeWords(name.split(" ")[0]);
      const msg = isBirthday
        ? `Happy Birthday ${firstName}! 🎂🎉 Wishing you a wonderful year ahead filled with joy and happiness!`
        : `Happy Anniversary ${firstName}! 💑🎉 Wishing you both many more years of love and togetherness!`;
      window.open(`https://wa.me/91${mobile}?text=${encodeURIComponent(msg)}`, "_blank");
    },
    [isBirthday]
  );

  const handleCall = useCallback((mobile) => {
    if (!mobile) return;
    window.open(`tel:${mobile}`);
  }, []);

  const activeCelebrant = filteredList[currentIndex] || null;

  return (
    <GlassCard>
      {/* ── Widget Top Bar ── */}
      <Box
        sx={{
          p: 2.5,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Category Selector (Birthdays vs Anniversaries) */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant={category === "Birthday" ? "contained" : "outlined"}
            size="small"
            onClick={() => setCategory("Birthday")}
            startIcon={<CakeOutlined />}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              ...(category === "Birthday"
                ? {
                  bgcolor: "#F59E0B",
                  color: "#fff",
                  boxShadow: "0 4px 14px rgba(245, 158, 11, 0.35)",
                  "&:hover": { bgcolor: "#D97706" },
                }
                : {
                  borderColor: alpha(theme.palette.divider, 0.3),
                  color: "text.primary",
                }),
            }}
          >
            Birthdays
            {bdayTotalCount > 0 && (
              <Chip
                label={bdayTotalCount}
                size="small"
                sx={{
                  ml: 0.8,
                  height: 18,
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  bgcolor: category === "Birthday" ? alpha("#fff", 0.3) : alpha("#F59E0B", 0.15),
                  color: category === "Birthday" ? "#fff" : "#D97706",
                }}
              />
            )}
          </Button>

          <Button
            variant={category === "Anniversary" ? "contained" : "outlined"}
            size="small"
            onClick={() => setCategory("Anniversary")}
            startIcon={<FavoriteOutlined />}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              ...(category === "Anniversary"
                ? {
                  bgcolor: "#EC4899",
                  color: "#fff",
                  boxShadow: "0 4px 14px rgba(236, 72, 153, 0.35)",
                  "&:hover": { bgcolor: "#BE185D" },
                }
                : {
                  borderColor: alpha(theme.palette.divider, 0.3),
                  color: "text.primary",
                }),
            }}
          >
            Anniversaries
            {annivTotalCount > 0 && (
              <Chip
                label={annivTotalCount}
                size="small"
                sx={{
                  ml: 0.8,
                  height: 18,
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  bgcolor: category === "Anniversary" ? alpha("#fff", 0.3) : alpha("#EC4899", 0.15),
                  color: category === "Anniversary" ? "#fff" : "#BE185D",
                }}
              />
            )}
          </Button>
        </Stack>

        {/* Right side: Day Filters & View Mode Toggles */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Day Filters: Today, Tomorrow, Both */}
          <Stack direction="row" spacing={0.6}>
            {[
              { id: "Today", label: "Today", count: stats.today, icon: Today },
              { id: "Tomorrow", label: "Tomorrow", count: stats.tomorrow, icon: DateRange },
              { id: "Both", label: "Today & Tomorrow", count: stats.total, icon: CelebrationOutlined },
            ].map(({ id, label, count, icon: Icon }) => {
              const active = dayFilter === id;
              return (
                <Chip
                  key={id}
                  icon={<Icon sx={{ fontSize: "14px !important", color: active ? "#fff !important" : `${primaryColor} !important` }} />}
                  label={`${label} (${count})`}
                  size="small"
                  onClick={() => setDayFilter(id)}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    cursor: "pointer",
                    borderRadius: 2,
                    ...(active
                      ? {
                        bgcolor: primaryColor,
                        color: "#fff",
                        boxShadow: `0 2px 8px ${alpha(primaryColor, 0.35)}`,
                      }
                      : {
                        bgcolor: alpha(theme.palette.action.hover, 0.4),
                        color: "text.secondary",
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        "&:hover": { bgcolor: alpha(primaryColor, 0.08) },
                      }),
                  }}
                />
              );
            })}
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: "center", display: { xs: "none", sm: "block" } }} />

          {/* Download PDF & Excel buttons */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip title={`Download ${category}s as Excel (.xlsx)`} arrow>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => exportCelebrationsToExcel(filteredList, category, dayFilter)}
                  disabled={filteredList.length === 0}
                  startIcon={<TableView sx={{ fontSize: "14px !important", color: "#16A34A" }} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    borderColor: alpha("#16A34A", 0.3),
                    color: "#16A34A",
                    bgcolor: alpha("#16A34A", 0.05),
                    "&:hover": { bgcolor: alpha("#16A34A", 0.12), borderColor: "#16A34A" },
                    height: 28,
                    px: 1,
                  }}
                >
                  Excel
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={`Download ${category}s as PDF Report`} arrow>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => exportCelebrationsToPDF(filteredList, category, dayFilter)}
                  disabled={filteredList.length === 0}
                  startIcon={<PictureAsPdf sx={{ fontSize: "14px !important", color: "#DC2626" }} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    borderColor: alpha("#DC2626", 0.3),
                    color: "#DC2626",
                    bgcolor: alpha("#DC2626", 0.05),
                    "&:hover": { bgcolor: alpha("#DC2626", 0.12), borderColor: "#DC2626" },
                    height: 28,
                    px: 1,
                  }}
                >
                  PDF
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: "center" }} />

          {/* View Mode: Card 1-by-1 vs List of All */}
          <Tooltip title={viewMode === "card" ? "Switch to List View" : "Switch to 1-by-1 Card View"} arrow>
            <Box
              sx={{
                display: "flex",
                bgcolor: alpha(theme.palette.action.hover, 0.4),
                borderRadius: 2,
                p: 0.3,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <IconButton
                size="small"
                onClick={() => setViewMode("card")}
                sx={{
                  borderRadius: 1.5,
                  p: 0.6,
                  bgcolor: viewMode === "card" ? alpha(primaryColor, 0.15) : "transparent",
                  color: viewMode === "card" ? primaryDark : "text.secondary",
                }}
              >
                <ViewCarouselOutlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setViewMode("list")}
                sx={{
                  borderRadius: 1.5,
                  p: 0.6,
                  bgcolor: viewMode === "list" ? alpha(primaryColor, 0.15) : "transparent",
                  color: viewMode === "list" ? primaryDark : "text.secondary",
                }}
              >
                <ViewListOutlined fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── Widget Body ── */}
      <Box sx={{ p: 2.5 }}>
        {loading ? (
          <Box sx={{ py: 3 }}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
          </Box>
        ) : filteredList.length === 0 ? (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
              borderRadius: 3,
              bgcolor: alpha(primaryColor, 0.03),
              border: `1px dashed ${alpha(primaryColor, 0.15)}`,
            }}
          >
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: "50%",
                mx: "auto",
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(primaryColor, 0.08),
                color: primaryColor,
              }}
            >
              <CelebrationOutlined sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              No {category.toLowerCase()} celebrations found for {dayFilter === "Both" ? "today & tomorrow" : dayFilter.toLowerCase()}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
              Check the other day tabs or switch between Birthdays and Anniversaries.
            </Typography>
          </Box>
        ) : viewMode === "card" ? (
          /* ══════════════════════════════════════════════════════════════════
             MODE 1: CARD-WISE ONE-BY-ONE CAROUSEL VIEW
             ══════════════════════════════════════════════════════════════════ */
          <Box>
            {activeCelebrant && (
              <ShowcaseCard primarycolor={primaryColor} primarydark={primaryDark}>
                <Grid container spacing={2.5} alignItems="center">
                  {/* Left: Avatar & Badges */}
                  <Grid item xs={12} sm="auto">
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <Box sx={{ position: "relative" }}>
                        <Avatar
                          src={
                            activeCelebrant.Picture
                              ? activeCelebrant.Picture.startsWith("http")
                                ? activeCelebrant.Picture
                                : `https://backend.dahiyainsurance.com/Upload/profile/${activeCelebrant.Picture}`
                              : undefined
                          }
                          sx={{
                            width: 80,
                            height: 80,
                            fontSize: "1.8rem",
                            fontWeight: 800,
                            bgcolor: alpha(primaryColor, 0.15),
                            color: primaryDark,
                            border: `3px solid ${primaryColor}`,
                            boxShadow: `0 6px 20px ${alpha(primaryColor, 0.35)}`,
                          }}
                        >
                          {activeCelebrant.FamilyMember?.[0]?.toUpperCase()}
                        </Avatar>
                        {activeCelebrant.DateCategory === "Today" && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -4,
                              right: -4,
                              bgcolor: primaryColor,
                              color: "#fff",
                              borderRadius: "50%",
                              p: 0.4,
                              display: "flex",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                              animation: `${pulseGlow} 2s infinite ease-in-out`,
                            }}
                          >
                            <AutoAwesome sx={{ fontSize: 13 }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Middle: Details & Meta */}
                  <Grid item xs={12} sm>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          label={activeCelebrant.DateCategory === "Today" ? "🎉 TODAY" : "TOMORROW"}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            fontWeight: 800,
                            bgcolor: activeCelebrant.DateCategory === "Today" ? primaryColor : alpha(primaryColor, 0.12),
                            color: activeCelebrant.DateCategory === "Today" ? "#fff" : primaryDark,
                            border: `1px solid ${alpha(primaryColor, 0.25)}`,
                          }}
                        />
                        {activeCelebrant.YearsCompleted && (
                          <Chip
                            label={
                              isBirthday
                                ? `${activeCelebrant.YearsCompleted} Years Completed`
                                : `${activeCelebrant.YearsCompleted}th Anniversary`
                            }
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: theme.palette.primary.main,
                            }}
                          />
                        )}
                        {activeCelebrant.Date && (
                          <Chip
                            label={`📅 ${isBirthday ? "Birth Date" : "Anniversary"}: ${moment(activeCelebrant.Date).format("DD MMM")}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor: alpha(primaryColor, 0.1),
                              color: primaryDark,
                              border: `1px solid ${alpha(primaryColor, 0.25)}`,
                            }}
                          />
                        )}
                        {activeCelebrant.OriginalDate && (
                          <Chip
                            label={`${isBirthday ? "🎂" : "💍"} Original Date: ${moment(activeCelebrant.OriginalDate).format("DD MMM YYYY")}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.action.hover, 0.5),
                              color: "text.primary",
                              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                            }}
                          />
                        )}
                      </Stack>

                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{
                          textTransform: "capitalize",
                          cursor: "pointer",
                          display: "inline-block",
                          "&:hover": { color: primaryDark },
                        }}
                        onClick={() => handleOpenModal(activeCelebrant)}
                      >
                        {capitalizeWords(activeCelebrant.FamilyMember)}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                        📞 {activeCelebrant.Mobile || "No mobile"}
                        {activeCelebrant.ID && ` • Main: ${activeCelebrant.MainMemberName} - ${activeCelebrant.ID}`}
                      </Typography>

                      {(activeCelebrant.Village || activeCelebrant.Tehsil || activeCelebrant.District || activeCelebrant.State) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.4 }}>
                          <LocationOnOutlined sx={{ fontSize: 13, color: primaryColor }} />
                          {[
                            activeCelebrant.Village && `Village: ${activeCelebrant.Village}`,
                            activeCelebrant.Tehsil && `Tehsil: ${activeCelebrant.Tehsil}`,
                            activeCelebrant.District && `Dist: ${activeCelebrant.District}`,
                            activeCelebrant.State && `State: ${activeCelebrant.State}`,
                          ].filter(Boolean).join(" • ")}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Right: Actions */}
                  <Grid item xs={12} md="auto">
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                      <Tooltip title="Send WhatsApp Wish" arrow>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleWhatsApp(activeCelebrant.Mobile, activeCelebrant.FamilyMember)}
                          startIcon={<WhatsApp sx={{ fontSize: 16 }} />}
                          sx={{
                            bgcolor: "#25D366",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.76rem",
                            borderRadius: 2,
                            textTransform: "none",
                            "&:hover": { bgcolor: "#1EBE5D" },
                            boxShadow: "0 3px 10px rgba(37, 211, 102, 0.3)",
                          }}
                        >
                          Wish
                        </Button>
                      </Tooltip>

                      <Tooltip title="Direct Call" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleCall(activeCelebrant.Mobile)}
                          sx={{
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            borderRadius: 2,
                            p: 0.8,
                            bgcolor: alpha(theme.palette.background.paper, 0.8),
                          }}
                        >
                          <Phone sx={{ fontSize: 16, color: "text.primary" }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="View Details in Popup Modal" arrow>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenModal(activeCelebrant)}
                          startIcon={<InfoOutlined sx={{ fontSize: 16 }} />}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.76rem",
                            borderColor: alpha(primaryColor, 0.4),
                            color: primaryDark,
                            "&:hover": { bgcolor: alpha(primaryColor, 0.06), borderColor: primaryColor },
                          }}
                        >
                          Details
                        </Button>
                      </Tooltip>

                      <Tooltip title="View Complete Profile" arrow>
                        <IconButton
                          component={Link}
                          to={
                            activeCelebrant.Type === "Agent"
                              ? `/CLIAProspect/EditCLIAProspect/${activeCelebrant.MainMemberID}`
                              : `/Prospects/ViewProspect/${activeCelebrant.MainMemberID}`
                          }
                          size="small"
                          sx={{
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            borderRadius: 2,
                            p: 0.8,
                            bgcolor: alpha(theme.palette.background.paper, 0.8),
                          }}
                        >
                          <OpenInNewOutlined sx={{ fontSize: 16, color: primaryDark }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </ShowcaseCard>
            )}

            {/* Stepper Navigation Footer (Prev / Next & Position) */}
            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Button
                size="small"
                variant="outlined"
                disabled={currentIndex === 0}
                onClick={handlePrev}
                startIcon={<NavigateBefore />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                }}
              >
                Previous
              </Button>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`${currentIndex + 1} of ${filteredList.length}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    bgcolor: alpha(primaryColor, 0.1),
                    color: primaryDark,
                  }}
                />
                <Button
                  size="small"
                  onClick={() => setViewMode("list")}
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  View All as List
                </Button>
              </Stack>

              <Button
                size="small"
                variant="outlined"
                disabled={currentIndex === filteredList.length - 1}
                onClick={handleNext}
                endIcon={<NavigateNext />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        ) : (
          /* ══════════════════════════════════════════════════════════════════
             MODE 2: LIST OF ALL CELEBRANTS
             ══════════════════════════════════════════════════════════════════ */
          <Box>
            <Grid container spacing={2}>
              {filteredList.map((item, idx) => {
                const isToday = item.DateCategory === "Today";
                return (
                  <Grid item xs={12} sm={6} md={4} key={`${item.ID || idx}-${idx}`}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        background:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.paper, 0.5)
                            : alpha("#ffffff", 0.8),
                        border: `1px solid ${alpha(
                          isToday ? primaryColor : theme.palette.divider,
                          isToday ? 0.35 : 0.1
                        )}`,
                        boxShadow: isToday ? `0 4px 14px ${alpha(primaryColor, 0.1)}` : "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 6px 18px ${alpha(primaryColor, 0.18)}`,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                        <Avatar
                          src={
                            item.Picture
                              ? item.Picture.startsWith("http")
                                ? item.Picture
                                : `https://backend.dahiyainsurance.com/Upload/profile/${item.Picture}`
                              : undefined
                          }
                          sx={{
                            width: 44,
                            height: 44,
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            bgcolor: alpha(primaryColor, 0.15),
                            color: primaryDark,
                            border: `2px solid ${alpha(primaryColor, 0.4)}`,
                            cursor: "pointer",
                          }}
                          onClick={() => handleOpenModal(item)}
                        >
                          {item.FamilyMember?.[0]?.toUpperCase()}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            noWrap
                            sx={{
                              textTransform: "capitalize",
                              cursor: "pointer",
                              "&:hover": { color: primaryDark },
                            }}
                            onClick={() => handleOpenModal(item)}
                          >
                            {capitalizeWords(item.FamilyMember)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                            {item.Mobile || "No mobile"}
                          </Typography>
                          {(item.Village || item.District) && (
                            <Typography variant="caption" color="text.disabled" noWrap sx={{ display: "block", fontSize: "0.68rem" }}>
                              📍 {[item.Village, item.District].filter(Boolean).join(", ")}
                            </Typography>
                          )}
                        </Box>

                        <Chip
                          label={isToday ? "TODAY" : "TOMORROW"}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            fontWeight: 800,
                            bgcolor: isToday ? primaryColor : alpha(primaryColor, 0.1),
                            color: isToday ? "#fff" : primaryDark,
                          }}
                        />
                      </Box>

                      <Stack direction="row" spacing={0.6} sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.5 }}>
                        {item.YearsCompleted && (
                          <Chip
                            label={
                              isBirthday
                                ? `${item.YearsCompleted} Yrs`
                                : `${item.YearsCompleted}th Anniv`
                            }
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.62rem",
                              fontWeight: 600,
                              bgcolor: alpha(primaryColor, 0.08),
                              color: primaryDark,
                            }}
                          />
                        )}
                        {item.Date && (
                          <Chip
                            label={`${isBirthday ? "Birth Date" : "Anniv"}: ${moment(item.Date).format("DD MMM")}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.62rem",
                              fontWeight: 600,
                              color: primaryDark,
                              bgcolor: alpha(primaryColor, 0.1),
                            }}
                          />
                        )}
                        {item.OriginalDate && (
                          <Chip
                            label={`Original: ${moment(item.OriginalDate).format("DD MMM YYYY")}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.62rem",
                              fontWeight: 600,
                              color: "text.secondary",
                              bgcolor: alpha(theme.palette.action.hover, 0.4),
                              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                            }}
                          />
                        )}
                      </Stack>

                      <Divider sx={{ opacity: 0.4, mb: 1.2 }} />

                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Wish on WhatsApp" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleWhatsApp(item.Mobile, item.FamilyMember)}
                              sx={{
                                color: "#25D366",
                                bgcolor: alpha("#25D366", 0.08),
                                p: 0.6,
                                "&:hover": { bgcolor: alpha("#25D366", 0.18) },
                              }}
                            >
                              <WhatsApp sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Call" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleCall(item.Mobile)}
                              sx={{
                                color: "text.secondary",
                                bgcolor: alpha(theme.palette.action.hover, 0.5),
                                p: 0.6,
                              }}
                            >
                              <Phone sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="View Popup Modal" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenModal(item)}
                              sx={{
                                color: primaryDark,
                                bgcolor: alpha(primaryColor, 0.08),
                                p: 0.6,
                              }}
                            >
                              <InfoOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Button
                          component={Link}
                          to={
                            item.Type === "Agent"
                              ? `/CLIAProspect/EditCLIAProspect/${item.MainMemberID}`
                              : `/Prospects/ViewProspect/${item.MainMemberID}`
                          }
                          size="small"
                          endIcon={<OpenInNewOutlined sx={{ fontSize: "12px !important" }} />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            color: "text.secondary",
                            "&:hover": { color: primaryDark },
                          }}
                        >
                          Profile
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Box>

      {/* ── Celebration Popup Modal ── */}
      <CelebrationDetailsDialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedItem(null);
        }}
        data={selectedItem}
        type={category}
      />
    </GlassCard>
  );
}
